import { formatCurrency } from "../../../../components/common/Converter";
import {
  GENERAL_PAYROLL_FIELDS,
  GSIS_LOAN_REPORT_FIELDS,
  HDMF_HOUSING_REMITTANCE_FIELDS,
  HDMF_LOAN_REPORT_FIELDS,
  HDMF_MP2_REMITTANCE_FIELDS,
  HDMF_PREMIUM_REMITTANCE_FIELDS,
} from "./payrollReportConfig";

export const CHUNK_SIZE = 100;

export function chunk(values, size = CHUNK_SIZE) {
  const chunks = [];
  for (let index = 0; index < values.length; index += size) {
    chunks.push(values.slice(index, index + size));
  }
  return chunks;
}

export function formatPeso(value) {
  const formatted = formatCurrency(String(value ?? 0));
  return formatted === "-" ? "" : `₱${formatted}`;
}

export function parseAmount(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const num = parseFloat(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(num) ? num : null;
}

/**
 * Longevity only applies when lengthOfService includes whole years
 * (e.g. "4 years, 18 days"). Values like "10 months" are skipped.
 */
export function getLongevityYears(lengthOfService) {
  if (!lengthOfService || typeof lengthOfService !== "string") return null;
  const match = lengthOfService.match(/(\d+)\s*years?/i);
  if (!match) return null;
  const years = Number(match[1]);
  return years > 0 ? years : null;
}

function formatMoneyField(raw) {
  if (typeof raw === "string" && raw.includes("₱")) return raw;
  return formatPeso(raw ?? 0);
}

export function withDerivedFields(
  objects = [],
  fields = [],
  {
    latestRatesByCode = new Map(),
    overpaymentByEmployee = new Map(),
    loanDeductionByEmployee = new Map(),
    coopDeductionByEmployee = new Map(),
    generalPayrollByEmployee = new Map(),
    gsisEmployeeShareByEmployee = new Map(),
    gsisLoansByEmployee = new Map(),
    hdmfEmployeeShareByEmployee = new Map(),
    hdmfLoansByEmployee = new Map(),
    phicEmployeeShareByEmployee = new Map(),
    taxByEmployee = new Map(),
    resolveMonthlyRate = true,
    resolveOverPayment = true,
    resolveLoanDeduction = true,
    resolveCoopDeduction = true,
    resolveGeneralPayroll = true,
    resolveRemittance = true,
    remittanceKind = null,
    resolveGsisLoans = true,
    resolvePaymentSummary = true,
    paymentSummaryKind = null,
    resolveHdmfPremiumRemittance = true,
    resolveHdmfLoans = true,
    resolveTax = true,
  } = {},
) {
  const needsMonthlyRate =
    resolveMonthlyRate &&
    (fields.includes("monthlyRate") || fields.includes("longevityPay"));
  const needsOverPayment = fields.includes("overPayment");
  const needsLoanDeduction = fields.includes("totalLoanDeduction");
  const needsCoopDeduction = fields.includes("totalCoopDeduction");
  const needsGeneralPayroll = GENERAL_PAYROLL_FIELDS.some(
    (field) => field !== "tax" && fields.includes(field),
  );
  const needsGsisRemittance = remittanceKind === "gsis";
  const needsPhilhealthRemittance = remittanceKind === "philhealth";
  const needsPhicRemittance = remittanceKind === "phic";
  const needsGsisLoans = GSIS_LOAN_REPORT_FIELDS.some((field) =>
    fields.includes(field),
  );
  const needsPaymentSummary =
    paymentSummaryKind === "gsis" || paymentSummaryKind === "hdmf";
  const needsHdmfPremiumRemittance = HDMF_PREMIUM_REMITTANCE_FIELDS.some(
    (field) => fields.includes(field),
  );
  const needsHdmfLoans = HDMF_LOAN_REPORT_FIELDS.some((field) =>
    fields.includes(field),
  );
  const needsHdmfMp2Remittance = HDMF_MP2_REMITTANCE_FIELDS.some((field) =>
    fields.includes(field),
  );
  const needsHdmfHousingRemittance = HDMF_HOUSING_REMITTANCE_FIELDS.some(
    (field) => fields.includes(field),
  );
  const needsTax = fields.includes("tax") && !needsGeneralPayroll;
  const needsLongevityRate = fields.includes("longevityRate");
  const needsLongevityPay = fields.includes("longevityPay");

  if (
    !needsMonthlyRate &&
    !needsOverPayment &&
    !needsLoanDeduction &&
    !needsCoopDeduction &&
    !needsGeneralPayroll &&
    !needsGsisRemittance &&
    !needsPhilhealthRemittance &&
    !needsPhicRemittance &&
    !needsGsisLoans &&
    !needsPaymentSummary &&
    !needsHdmfPremiumRemittance &&
    !needsHdmfLoans &&
    !needsHdmfMp2Remittance &&
    !needsHdmfHousingRemittance &&
    !needsTax &&
    !needsLongevityRate &&
    !needsLongevityPay
  ) {
    return objects;
  }

  objects.forEach((object) => {
    if (needsMonthlyRate) {
      if (object?.nonRegPosition) {
        object.monthlyRate = object.nonRegPosition.montlhyRate ?? "";
      } else {
        object.monthlyRate = object?.salaryCode
          ? latestRatesByCode.get(object.salaryCode) ?? ""
          : "";
      }
    }

    if (needsOverPayment) {
      object.overPayment = formatMoneyField(
        resolveOverPayment
          ? overpaymentByEmployee.get(object?.id) ?? 0
          : object.overPayment,
      );
    }

    if (needsLoanDeduction) {
      object.totalLoanDeduction = formatMoneyField(
        resolveLoanDeduction
          ? loanDeductionByEmployee.get(object?.id) ?? 0
          : object.totalLoanDeduction,
      );
    }

    if (needsCoopDeduction) {
      object.totalCoopDeduction = formatMoneyField(
        resolveCoopDeduction
          ? coopDeductionByEmployee.get(object?.id) ?? 0
          : object.totalCoopDeduction,
      );
    }

    if (needsGeneralPayroll) {
      const payroll = generalPayrollByEmployee.get(object?.id) ?? {};
      GENERAL_PAYROLL_FIELDS.forEach((field) => {
        if (!fields.includes(field)) return;
        object[field] = formatMoneyField(
          resolveGeneralPayroll ? payroll[field] ?? 0 : object[field],
        );
      });
    }

    if (needsGsisRemittance) {
      const employeeShare = formatMoneyField(
        resolveRemittance
          ? gsisEmployeeShareByEmployee.get(object?.id) ?? 0
          : object.employeeShare,
      );
      object.employeeShare = employeeShare;
      object.employerShare = "";
      object.totalGsis = employeeShare;
    }

    if (needsPhilhealthRemittance) {
      const shareRaw = resolveRemittance
        ? phicEmployeeShareByEmployee.get(object?.id) ?? 0
        : object.employeeShare;
      const shareAmount = parseAmount(shareRaw) ?? 0;
      const employeeShare = formatMoneyField(shareRaw);
      object.employeeShare = employeeShare;
      object.employerShare = employeeShare;
      object.totalPremium = formatMoneyField(shareAmount * 2);
    }

    if (needsPhicRemittance) {
      const shareRaw = resolveRemittance
        ? phicEmployeeShareByEmployee.get(object?.id) ?? 0
        : object.employeePremium;
      const shareAmount = parseAmount(shareRaw) ?? 0;
      const premium = formatMoneyField(shareRaw);
      object.employeePremium = premium;
      object.employerPremium = premium;
      object.totalPremium = formatMoneyField(shareAmount * 2);
    }

    if (needsGsisLoans) {
      const loans = gsisLoansByEmployee.get(object?.id) ?? {};
      GSIS_LOAN_REPORT_FIELDS.forEach((field) => {
        if (!fields.includes(field)) return;
        object[field] = formatMoneyField(
          resolveGsisLoans ? loans[field] ?? 0 : object[field],
        );
      });
    }

    if (needsPaymentSummary) {
      const premium = resolvePaymentSummary
        ? paymentSummaryKind === "hdmf"
          ? hdmfEmployeeShareByEmployee.get(object?.id) ?? 0
          : gsisEmployeeShareByEmployee.get(object?.id) ?? 0
        : object.premiums;
      const loan = resolvePaymentSummary
        ? paymentSummaryKind === "hdmf"
          ? hdmfLoansByEmployee.get(object?.id)?.totalHdmfLoan ?? 0
          : loanDeductionByEmployee.get(object?.id) ?? 0
        : object.loans;
      const total = resolvePaymentSummary
        ? (Number(premium) || 0) + (Number(loan) || 0)
        : object.totalPayment;

      object.premiums = formatMoneyField(premium);
      object.loans = formatMoneyField(loan);
      object.totalPayment = formatMoneyField(total);
    }

    if (needsHdmfPremiumRemittance) {
      const employeeContribution = formatMoneyField(
        resolveHdmfPremiumRemittance
          ? hdmfEmployeeShareByEmployee.get(object?.id) ?? 0
          : object.employeeContribution,
      );
      object.employeeContribution = employeeContribution;
      object.employerContribution = "";
      object.Total = employeeContribution;
    }

    if (needsHdmfLoans) {
      const loans = hdmfLoansByEmployee.get(object?.id) ?? {};
      HDMF_LOAN_REPORT_FIELDS.forEach((field) => {
        if (!fields.includes(field)) return;
        object[field] = formatMoneyField(
          resolveHdmfLoans ? loans[field] ?? 0 : object[field],
        );
      });
    }

    if (needsHdmfMp2Remittance) {
      HDMF_MP2_REMITTANCE_FIELDS.forEach((field) => {
        if (!fields.includes(field)) return;
        object[field] = formatMoneyField(0);
      });
    }

    if (needsHdmfHousingRemittance) {
      HDMF_HOUSING_REMITTANCE_FIELDS.forEach((field) => {
        if (!fields.includes(field)) return;
        object[field] = formatMoneyField(0);
      });
    }

    if (needsTax) {
      object.tax = formatMoneyField(
        resolveTax ? taxByEmployee.get(object?.id) ?? 0 : object.tax,
      );
    }

    if (needsLongevityRate || needsLongevityPay) {
      const years = getLongevityYears(object?.lengthOfService);

      if (years == null) {
        if (needsLongevityRate) object.longevityRate = "";
        if (needsLongevityPay) object.longevityPay = "";
        return;
      }

      if (needsLongevityRate) object.longevityRate = `${years}%`;

      if (needsLongevityPay) {
        const monthlyRate = parseAmount(object?.monthlyRate);
        object.longevityPay =
          monthlyRate == null ? "" : formatPeso(monthlyRate * (years / 100));
      }
    }
  });

  return objects;
}
